import msalInstance from './msalInstance';
import { loginRequest } from './msalConfig';

const authService = {
  _initialized: false,
  _initializePromise: null,
  _idToken: null,
  _crossTabLogoutInProgress: false,

  async initialize() {
    this._initializePromise = (async () => {
      await msalInstance.initialize();
      this._initialized = true;
      try {
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          msalInstance.setActiveAccount(response.account);
          this._idToken = response.idToken;
        }
      } catch (error) {
        console.error('MSAL redirect error:', error);
      }
    })();
    return this._initializePromise;
  },

  login() {
    return msalInstance.loginRedirect(loginRequest);
  },

  async logout() {
    // Ensure handleRedirectPromise() has completed before starting a new interaction
    if (this._initializePromise) await this._initializePromise;
    const account = this.getAccount();
    // If _idToken wasn't captured at login (e.g. page was refreshed), acquire it silently
    if (!this._idToken && account) {
      try {
        const response = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
        this._idToken = response.idToken;
      } catch {
        // proceed without idTokenHint if silent acquisition fails
      }
    }
    // postLogoutRedirectUri is set globally in msalConfig
    return msalInstance.logoutRedirect({ account: account ?? undefined, idTokenHint: this._idToken ?? undefined });
  },

  // Registers a storage-event listener for instant cross-tab / cross-app logout.
  // When B2C calls the front-channel logout URI (/logout) in a hidden iframe,
  // clearCache() removes MSAL keys from localStorage. Every open tab receives
  // a 'storage' event and immediately triggers a fresh B2C logout redirect.
  setupCrossTabLogout() {
    const knownAuthority = process.env.VUE_APP_MSAL_KNOWN_AUTHORITY;
    if (!knownAuthority) {
      console.error('VUE_APP_MSAL_KNOWN_AUTHORITY is not set; cross-tab logout disabled.');
      return;
    }
    window.addEventListener('storage', async (event) => {
      if (!event.key || event.newValue !== null) return;
      if (this._crossTabLogoutInProgress) return;
      if (event.key.includes('|' + knownAuthority + '|') && event.key.includes('idtoken')) {
        this._crossTabLogoutInProgress = true;
        try {
          await this.logout();
        } catch (e) {
          console.error('[Auth] Cross-tab logout failed:', e);
          this._crossTabLogoutInProgress = false;
        }
      }
    });
  },

  // Called by the /logout front-channel logout route when B2C triggers SLO.
  // Must NOT redirect — it runs inside a hidden iframe.
  async clearCache() {
    if (this._initializePromise) await this._initializePromise;
    this._idToken = null;
    return msalInstance.clearCache();
  },

  async getAccessToken() {
    const account = this.getAccount();
    if (!account) {
      throw new Error('No active account. Please login.');
    }
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      // If silent acquisition fails, fallback to redirect
      await msalInstance.acquireTokenRedirect(loginRequest);
      return null;
    }
  },
  isAuthenticated() {
    if (!this._initialized) return false;
    return msalInstance.getAllAccounts().length > 0;
  },

  getAccount() {
    if (!this._initialized) return null;
    const activeAccount = msalInstance.getActiveAccount();
    if (activeAccount) return activeAccount;

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
      return accounts[0];
    }
    return null;
  },
  // Clears the local MSAL token cache WITHOUT triggering B2C SLO.
  // Used before crossover/NoAccessURL redirects so the blocked user can
  // log in with a different account when they return to the app.
  // Clears the ENTIRE cache (not just one account) and resets the active
  // account, because cacheLocation is 'localStorage' and therefore shared
  // across tabs — a stale blocked account would otherwise keep other tabs
  // "authenticated" and re-trigger the crossover redirect.
  async clearLocalSession() {
    try {
      // No account arg => clears all cached accounts/tokens for this app.
      await msalInstance.clearCache();
    } catch (e) {
      // clearCache is best-effort — proceed with redirect regardless.
      console.warn('[authService] clearLocalSession failed:', e);
    }
    try {
      msalInstance.setActiveAccount(null);
    } catch (e) {
      // ignore — best effort
    }
    localStorage.removeItem('user');
    localStorage.removeItem('userView');
    localStorage.removeItem('toolData');
    localStorage.removeItem('hasRequestedVAM');
    localStorage.removeItem('hasRequestedETAM');
  },
};

export default authService;
